import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { clients } from '../mock';

const Clients = () => {
  const orderedClients = useMemo(
    () => [...clients].sort((a, b) => a.position - b.position),
    []
  );

  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  // Determine how many cards to show/scroll per view based on viewport width.
  const getVisibleCount = () => {
    if (typeof window === 'undefined') return 3;
    const w = window.innerWidth;
    if (w >= 1280) return 6;
    if (w >= 1024) return 5;
    if (w >= 768) return 4;
    if (w >= 640) return 3;
    return 3; // small mobile: 3 cards
  };

  const [visibleCount, setVisibleCount] = useState(getVisibleCount);

  useEffect(() => {
    const onResize = () => setVisibleCount(getVisibleCount());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Track scroll position to enable/disable arrows
  const updateArrowState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrowState();
    el.addEventListener('scroll', updateArrowState, { passive: true });
    window.addEventListener('resize', updateArrowState);
    return () => {
      el.removeEventListener('scroll', updateArrowState);
      window.removeEventListener('resize', updateArrowState);
    };
  }, [updateArrowState, visibleCount]);

  const scrollByPage = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    // Move by 3 cards at a time (as requested) on any breakpoint.
    // On smaller viewports the user still swipes naturally with the finger.
    const card = el.querySelector('[data-card]');
    if (!card) return;
    const styles = window.getComputedStyle(el);
    const gap = parseInt(styles.columnGap || styles.gap || '16', 10) || 16;
    const step = (card.offsetWidth + gap) * 3;
    el.scrollBy({ left: dir * step, behavior: 'smooth' });
  };

  return (
    <section id="clientes" className="py-24 px-6 bg-white">
      <div className="max-w-[1400px] mx-auto">
        <div className="text-center mb-14">
          <p
            className="text-[#C99A36] tracking-[0.4em] text-[12px] font-semibold mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            CLIENTES
          </p>
          <h2
            className="text-[#1a1a1a] text-[36px] md:text-[44px] font-bold tracking-tight"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Clientes
          </h2>
          <div className="mx-auto mt-4 w-24 h-[3px] bg-[#C99A36]" />
        </div>

        {/* Carousel container */}
        <div className="relative">
          {/* Prev arrow (hidden on mobile - touch swipe instead) */}
          <button
            onClick={() => scrollByPage(-1)}
            disabled={!canPrev}
            aria-label="Clientes anteriores"
            className={`hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 items-center justify-center w-12 h-12 rounded-full bg-[#0e0e0e] text-white shadow-lg transition-all duration-300 ${
              canPrev ? 'opacity-100 hover:bg-[#C99A36] hover:scale-105' : 'opacity-30 cursor-not-allowed'
            }`}
          >
            <ChevronLeft size={22} />
          </button>

          {/* Scrollable track */}
          <div
            ref={scrollerRef}
            className="flex gap-4 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-4 -mx-2 px-2 clients-scroller"
            style={{
              scrollbarWidth: 'thin',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {orderedClients.map((c) => (
              <div
                key={c.position}
                data-card
                className="snap-start flex-shrink-0 aspect-square bg-[#3a3a3a] flex items-center justify-center overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group"
                style={{
                  // Card width calculated so exactly `visibleCount` fit with gaps
                  flex: `0 0 calc((100% - ${(visibleCount - 1) * 16}px) / ${visibleCount})`,
                }}
                title={c.name}
              >
                <img
                  src={c.logo}
                  alt={c.name}
                  loading="lazy"
                  draggable={false}
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ))}
          </div>

          {/* Next arrow */}
          <button
            onClick={() => scrollByPage(1)}
            disabled={!canNext}
            aria-label="Próximos clientes"
            className={`hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 items-center justify-center w-12 h-12 rounded-full bg-[#0e0e0e] text-white shadow-lg transition-all duration-300 ${
              canNext ? 'opacity-100 hover:bg-[#C99A36] hover:scale-105' : 'opacity-30 cursor-not-allowed'
            }`}
          >
            <ChevronRight size={22} />
          </button>
        </div>

        {/* Mobile helper text */}
        <p
          className="mt-4 text-center text-[11px] text-[#888] tracking-[0.2em] uppercase md:hidden"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Arraste para o lado
        </p>
      </div>
    </section>
  );
};

export default Clients;
